const normalizeResearcherNames = researchers =>
  (Array.isArray(researchers) ? researchers : [])
    .map(researcher => {
      if (typeof researcher === 'string') {
        return researcher;
      }
      const firstName = researcher?.member?.first_name || '';
      const lastName = researcher?.member?.last_name || '';
      const memberName = `${firstName} ${lastName}`.trim();
      return researcher?.name || memberName || researcher?.email || researcher?.member?.email || '';
    })
    .map(name => `${name || ''}`.trim())
    .filter(Boolean);

const normalizeStringArray = values =>
  (Array.isArray(values) ? values : []).map(value => `${value || ''}`.trim()).filter(Boolean);

const getSearchObject = item => {
  if (item?.object && typeof item.object === 'object') {
    return item.object;
  }
  return item && typeof item === 'object' ? item : {};
};

const getFirstDefined = (...values) => values.find(value => typeof value !== 'undefined');

const getFirstNonEmptyString = (...values) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
};

const toCamelCase = value => `${value || ''}`.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

const normalizeResearchClientResearchers = researchers =>
  (Array.isArray(researchers) ? researchers : [])
    .filter(researcher => researcher && typeof researcher === 'object')
    .map(researcher => ({
      id: researcher.id || null,
      member_id: researcher.member_id || null,
      name: researcher.name || '',
      email: researcher.email || '',
    }));

const normalizeResearchClientNotes = notes =>
  (Array.isArray(notes) ? notes : [])
    .filter(note => note && typeof note === 'object')
    .map(note => ({
      id: note.id || null,
      title: note.title || '',
      tags: normalizeStringArray(note.tags),
    }));

const normalizeResearchClientContacts = contacts =>
  (Array.isArray(contacts) ? contacts : [])
    .filter(contact => contact && typeof contact === 'object')
    .map(contact => {
      const firstName = contact?.member?.first_name || '';
      const lastName = contact?.member?.last_name || '';
      const memberName = `${firstName} ${lastName}`.trim();
      const contactFirstName = contact.first_name || '';
      const contactLastName = contact.last_name || '';
      const contactDisplay = contact.display_name || contact.display || '';
      return {
        id: contact.id || null,
        member_id: contact.member_id || null,
        member: contact.member || null,
        first_name: contactFirstName,
        last_name: contactLastName,
        display_name: contact.display_name || '',
        display: contactDisplay || memberName,
        email: contact.email || contact.member?.email || '',
        hubspot_contact_id: contact.hubspot_contact_id || '',
        is_main: !!contact.is_main,
        created_at: contact.created_at || null,
        updated_at: contact.updated_at || null,
      };
    })
    .filter(
      contact =>
        contact.display ||
        contact.display_name ||
        contact.email ||
        contact.member_id ||
        contact.member ||
        contact.hubspot_contact_id
    );

const normalizeMatchedBitDates = dates =>
  (Array.isArray(dates) ? dates : [])
    .map(date => {
      if (typeof date === 'string') {
        const normalizedDate = date.trim();
        const parsedDate = new Date(normalizedDate);
        if (!Number.isNaN(parsedDate.getTime())) {
          return parsedDate.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          });
        }
        return normalizedDate;
      }
      if (date && typeof date === 'object') {
        return getFirstNonEmptyString(date.label, date.display, date.text, date.value);
      }
      return '';
    })
    .filter(Boolean);

const normalizeHighlightValues = values =>
  (Array.isArray(values) ? values : []).map(value => `${value || ''}`.trim()).filter(Boolean);

const normalizeMatchedBitHighlight = highlight => {
  if (!highlight || typeof highlight !== 'object') {
    return {};
  }

  return Object.entries(highlight).reduce((acc, [key, value]) => {
    const normalizedValues = normalizeHighlightValues(value);
    if (normalizedValues.length) {
      acc[toCamelCase(key)] = normalizedValues;
    }
    return acc;
  }, {});
};

const normalizeMatchedSearchBits = bits =>
  (Array.isArray(bits) ? bits : [])
    .filter(bit => bit && typeof bit === 'object')
    .map(bit => ({
      title: getFirstNonEmptyString(bit.title),
      tags: normalizeStringArray(bit.tags),
      type: getFirstNonEmptyString(bit.type),
      category: getFirstNonEmptyString(bit.category),
      contentText: getFirstNonEmptyString(bit.content_text),
      englishContent: getFirstNonEmptyString(bit.english_content),
      chineseContent: getFirstNonEmptyString(bit.chinese_content),
      dateStart: getFirstDefined(bit.date_start, null),
      dateEnd: getFirstDefined(bit.date_end, null),
      dates: normalizeMatchedBitDates(bit.dates),
      researchClientId: getFirstDefined(bit.research_client_id, null),
      contacts: normalizeResearchClientContacts(bit.contacts),
      researchers: normalizeResearchClientResearchers(bit.researchers),
      highlight: normalizeMatchedBitHighlight(bit.highlight),
    }))
    .filter(
      bit =>
        bit.title ||
        bit.tags.length ||
        bit.type ||
        bit.category ||
        bit.contentText ||
        bit.englishContent ||
        bit.chineseContent ||
        bit.dates.length ||
        bit.researchClientId ||
        bit.contacts.length ||
        bit.researchers.length ||
        bit.dateStart ||
        bit.dateEnd
    );

const getFirstContact = (...contactLists) => {
  for (const contacts of contactLists) {
    if (Array.isArray(contacts) && contacts.length) {
      return contacts[0];
    }
  }
  return null;
};

const normalizeResearcherMembers = client => {
  if (Array.isArray(client?.research_members)) {
    return client.research_members;
  }
  return Array.isArray(client?.researchers)
    ? client.researchers.filter(researcher => researcher && typeof researcher === 'object')
    : [];
};

const getResearchClientProjectsCount = client => {
  if (Number.isFinite(client?.projects_count)) {
    return client.projects_count;
  }
  return 0;
};

const getResearchClientSkillSessionsCount = client => {
  if (Number.isFinite(client?.skill_sessions_count)) {
    return client.skill_sessions_count;
  }
  return 0;
};

export const normalizeResearchClient = (client, membership = null) => {
  if (!client?.id) {
    return null;
  }

  return {
    id: client.id,
    member_id: client.member_id || null,
    name: client.name || '',
    projects_count: getResearchClientProjectsCount(client),
    skill_sessions_count: getResearchClientSkillSessionsCount(client),
    membership_id: membership?.id || null,
    membership_status: membership?.status || 'current',
    sharepoint_folder: client.sharepoint_folder || '',
    archive_folder: client.archive_folder || '',
    researchers: normalizeResearcherNames(client.researchers),
    contacts: normalizeResearchClientContacts(client.contacts),
    related_clients: Array.isArray(client.related_clients) ? client.related_clients : [],
    created_at: client.created_at || null,
    updated_at: client.updated_at || null,
    research_members: normalizeResearcherMembers(client),
  };
};

export const normalizeManualResearchClient = client => {
  if (!client?.id && !client?.member_id) {
    return null;
  }

  return {
    id: client.id || '',
    member_id: client.member_id || '',
    membership_id: client.membership_id || client.membership?.id || null,
    membership_status: client.membership_status || client.membership?.status || null,
    name: client.name || '',
    projects_count: getResearchClientProjectsCount(client),
    skill_sessions_count: getResearchClientSkillSessionsCount(client),
    researchers: normalizeResearcherNames(client.researchers),
    contacts: normalizeResearchClientContacts(client.contacts),
    related_clients: Array.isArray(client.related_clients) ? client.related_clients : [],
    sharepoint_folder: client.sharepoint_folder || '',
    archive_folder: client.archive_folder || '',
    created_at: client.created_at || null,
    updated_at: client.updated_at || null,
    research_members: normalizeResearcherMembers(client),
  };
};

const getUnifiedSearchBase = (item, currentItem = null) => {
  const source = item && typeof item === 'object' ? item : {};
  const current = currentItem && typeof currentItem === 'object' ? currentItem : {};
  const sourceObject = getSearchObject(source);
  const currentObject = getSearchObject(current);
  const id = getFirstDefined(source.object_id, source.id, current.id, null);
  const kind = getFirstNonEmptyString(source.kind, source.type, current.kind, current.type);
  const authorId = getFirstDefined(
    source.author_id,
    sourceObject.author_id,
    current.authorId,
    currentObject.author_id,
    null
  );
  const createdAt = getFirstDefined(source.created_at, current.createdAt, null);
  const updatedAt = getFirstDefined(source.updated_at, current.updatedAt, null);
  const coverImageThumbnail = getFirstNonEmptyString(source.cover_image_thumbnail, current.coverImageThumbnail);
  const sourceContacts = normalizeResearchClientContacts(sourceObject.contacts);
  const currentContacts = normalizeResearchClientContacts(currentObject.contacts);
  const firstContact = getFirstContact(sourceContacts, current.contacts, currentContacts);
  const familyTreeId = getFirstDefined(
    firstContact?.member?.tree_id,
    current.familyTreeId,
    sourceObject.family_tree_id,
    source.family_tree_id,
    currentObject.family_tree_id,
    null
  );
  const matchedSearchBits = normalizeMatchedSearchBits(
    getFirstDefined(source.matched_search_bits, current.matchedSearchBits, [])
  );

  return {
    id,
    kind,
    type: kind,
    object: source.object && typeof source.object === 'object' ? source.object : sourceObject,
    raw: source,
    title: getFirstNonEmptyString(source.title, sourceObject.title, sourceObject.name, current.title) || `${id || ''}`,
    authorId,
    authorName: getFirstNonEmptyString(
      sourceObject.author_name,
      source.author_name,
      current.authorName,
      currentObject.author_name
    ),
    createdAt,
    updatedAt,
    coverImageThumbnail,
    thumbnail: coverImageThumbnail || null,
    familyTreeId,
    contacts: sourceContacts,
    matchedSearchBits,
  };
};

const normalizeCommonUnifiedSearchItem = (item, currentItem = null) => {
  const source = item && typeof item === 'object' ? item : {};
  const current = currentItem && typeof currentItem === 'object' ? currentItem : {};
  const sourceObject = getSearchObject(source);
  const currentObject = getSearchObject(current);
  const base = getUnifiedSearchBase(source, current);

  return {
    ...base,
    tags: normalizeStringArray(getFirstDefined(sourceObject.tags, source.tags, current.tags, currentObject.tags, [])),
    slug: getFirstNonEmptyString(sourceObject.slug, source.slug, current.slug, currentObject.slug),
    category: getFirstNonEmptyString(sourceObject.category, source.category, current.category, currentObject.category),
    noteType: getFirstNonEmptyString(sourceObject.note_type, source.note_type, current.noteType, current.note_type),
    dateStart: getFirstDefined(sourceObject.date_start, source.date_start, current.dateStart, null),
    dateEnd: getFirstDefined(sourceObject.date_end, source.date_end, current.dateEnd, null),
    content: getFirstNonEmptyString(sourceObject.content, source.content, current.content, currentObject.content),
    dateText: getFirstNonEmptyString(sourceObject.date_text, source.date_text, current.dateText, current.date_text),
  };
};

const normalizeResearchNoteUnifiedSearchItem = (item, currentItem = null) => {
  const source = item && typeof item === 'object' ? item : {};
  const current = currentItem && typeof currentItem === 'object' ? currentItem : {};
  const sourceObject = getSearchObject(source);
  const currentObject = getSearchObject(current);
  const commonItem = normalizeCommonUnifiedSearchItem(source, current);

  return {
    ...commonItem,
    location: getFirstDefined(sourceObject.location, source.location, current.location, currentObject.location, null),
    researchClient: getFirstDefined(
      sourceObject.research_client,
      source.research_client,
      current.researchClient,
      currentObject.research_client,
      null
    ),
  };
};

const normalizeResearchClientUnifiedSearchItem = (item, currentItem = null) => {
  const source = item && typeof item === 'object' ? item : {};
  const current = currentItem && typeof currentItem === 'object' ? currentItem : {};
  const sourceObject = getSearchObject(source);
  const commonItem = normalizeCommonUnifiedSearchItem(source, current);

  return {
    ...commonItem,
    subtitle: getFirstNonEmptyString(current.subtitle),
    description: getFirstNonEmptyString(current.description),
    memberId: getFirstDefined(
      commonItem.contacts[0]?.member_id,
      sourceObject.member_id,
      source.member_id,
      current.memberId,
      null
    ),
    researchers: normalizeResearchClientResearchers(sourceObject.researchers),
    projects: Array.isArray(sourceObject.projects) ? sourceObject.projects : [],
    researchNoteIds: Array.isArray(sourceObject.research_note_ids) ? sourceObject.research_note_ids : [],
    researchNotes: normalizeResearchClientNotes(sourceObject.research_notes),
  };
};

const normalizeStoryUnifiedSearchItem = (item, currentItem = null) => {
  const source = item && typeof item === 'object' ? item : {};
  const current = currentItem && typeof currentItem === 'object' ? currentItem : {};
  const sourceObject = getSearchObject(source);
  const currentObject = getSearchObject(current);
  const commonItem = normalizeCommonUnifiedSearchItem(source, current);

  return {
    ...commonItem,
    isPublished: getFirstDefined(
      sourceObject.is_published,
      source.is_published,
      current.isPublished,
      currentObject.is_published,
      false
    ),
    member: getFirstNonEmptyString(sourceObject.member, source.member, current.member, currentObject.member),
    memberId: getFirstDefined(
      sourceObject.member_id,
      source.member_id,
      current.memberId,
      currentObject.member_id,
      null
    ),
    status: getFirstNonEmptyString(sourceObject.status, source.status, current.status, currentObject.status),
  };
};

const normalizeCompilationUnifiedSearchItem = (item, currentItem = null) =>
  normalizeCommonUnifiedSearchItem(item, currentItem);

export const normalizeResearchUnifiedSearchItem = (item, currentItem = null) => {
  const base = getUnifiedSearchBase(item, currentItem);

  switch (base.kind) {
    case 'research_note':
      return normalizeResearchNoteUnifiedSearchItem(item, currentItem);
    case 'research_client':
      return normalizeResearchClientUnifiedSearchItem(item, currentItem);
    case 'story':
      return normalizeStoryUnifiedSearchItem(item, currentItem);
    case 'compilation':
      return normalizeCompilationUnifiedSearchItem(item, currentItem);
    default:
      return normalizeCommonUnifiedSearchItem(item, currentItem);
  }
};

export const normalizeResearchUnifiedSearchItems = items =>
  (Array.isArray(items) ? items : []).map(item => normalizeResearchUnifiedSearchItem(item));
